/* @jsx h */
import { defineComponent, h, onMounted, ref, type PropType } from "vue";
import { NumPad } from "../NumPad";
import { LemmonModal } from "../Modal";
import type { GridColumnDef, CommitDir } from "./types";

export const NumberEditor = defineComponent({
  name: "NumberEditor",
  props: {
    column: { type: Object as PropType<GridColumnDef>, required: true },
    value: { type: [Number, String, null] as any, default: null },
    initialChar: { type: String as PropType<string | null>, default: null },
    onCommit: {
      type: Function as PropType<(value: any, dir: CommitDir) => void>,
      required: true,
    },
    onCancel: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    // Detect mobile synchronously so there's no flash between modes
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const inputRef = ref<HTMLInputElement>();

    const initStr =
      props.initialChar !== null
        ? props.initialChar
        : props.value !== null && props.value !== undefined
          ? String(props.value)
          : "";

    const draft = ref<string>(initStr);
    const numpadDraft = ref<string>(initStr);

    onMounted(() => {
      if (!isMobile && inputRef.value) {
        inputRef.value.focus();
        inputRef.value.select();
      }
    });

    const parseNum = (str: string) => {
      if (!str) return null;
      const n = parseFloat(str);
      return isNaN(n) ? null : n;
    };

    const commit = (dir: CommitDir = null) => props.onCommit(parseNum(draft.value), dir);
    const commitNumpad = () => props.onCommit(parseNum(numpadDraft.value), null);

    if (isMobile) {
      return () => (
        <LemmonModal title={props.column.header} onClose={props.onCancel}>
          <div class="space-y-4 p-4">
            <NumPad
              value={numpadDraft.value}
              onChange={(v: string) => (numpadDraft.value = v)}
            />
            <button class="btn btn-primary w-full rounded-xl" onClick={commitNumpad}>
              Confirm
            </button>
          </div>
        </LemmonModal>
      );
    }

    return () => (
      <input
        ref={inputRef}
        type="number"
        class="lg-editor-input"
        value={draft.value}
        step={props.column.step ?? "any"}
        min={props.column.min}
        placeholder={props.column.placeholder ?? ""}
        onInput={(e: any) => (draft.value = e.target.value)}
        onBlur={() => commit(null)}
        onKeydown={(e: KeyboardEvent) => {
          if (e.key === "Escape") { e.stopPropagation(); props.onCancel(); }
          if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); commit("down"); }
          if (e.key === "Tab") { e.preventDefault(); e.stopPropagation(); commit(e.shiftKey ? "left" : "right"); }
        }}
      />
    );
  },
});
